/* eslint-disable no-undefined */
import { Injectable, OnDestroy } from '@angular/core';
import TrimbleMaps, {
  Common,
  DateTimeType,
  RouteOptions,
} from '@trimblemaps/trimblemaps-js';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  mergeMap,
} from 'rxjs/operators';
import {
  ArrDepSelection,
  Route,
  StopTimeStamp,
  getEtaEtdDateTime,
  serializeArrDepSelection,
  deserializeArrDepSelection,
} from '../_models/view/route';
import { RouteEditMode, RouteState } from '../_models/state/routeState';
import { MapRoute, RouteOptimizationEvent } from '../_models/view/mapRoute';
import { StopLocation, StopState } from '../_models/state/stopState';
import { cloneDeep, isEqual } from 'lodash-es';
import { MapService } from 'src/app/services/map.service';
import { routePathOpacity } from 'src/app/_helpers/route-path-opacity';
import { RoutingProfileService } from './vehicle-routing-profile.service';
import * as DaTrip from '../_models/daws/trip';
import { ConversionService } from 'src/app/services/conversion.service';
import { moveItemInArray } from '@angular/cdk/drag-drop';
import { SubscriptionManager } from '../_helpers/subscription-manager';
import { getHoursFromString } from '../_helpers/stop-duration-helper';
import { RouteOptimization } from '../_models/trimblemaps';
import { RouteStop } from '../_models/view/routeStop';
import { AuthenticationService } from '../auth/services/authentication.service';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BrazeService } from './braze.service';
import { EUProfile, RoutingProfile } from '../_models/daws/trip';
import { SubscriptionLevel } from '../auth/models/user';
import { getDayOfWeek, getTimeZone } from '../utils/dayOfWeek';
import { EstimatedTimeOptionsStruct } from '../_models/pcmws/arriveDepart/server_side_structs';
import { AlertService } from '../auth/services/alert.service';
import {
  RoutingProfileModel,
  VehicleTypeEnum,
} from '../_models/profilews/profilews';
import { ApplicationSettingsService } from './application-settings.service';
import { UserService } from './user.service';
import { DataVersionService } from './data-version.service';
import { NumericKeyValueState } from '../_models/state/optionUIState';

interface RouteStore {
  routeList: RouteState[];
  selectedRoute: RouteState;
  availableRouteColors: string[];
  lastId: number;
}

const ROUTE_COLORS = [
  '#488BE0',
  '#62A82F',
  '#F69134',
  '#AF69FF',
  '#43D1C5',
  '#E580B3',
  '#C49A00',
  '#AD6B50',
];

@Injectable({
  providedIn: 'root',
})
export class RouteService implements OnDestroy {
  // Most of this would be better handled with a Store library like ngrx/ngrx-component
  private isLoading = new BehaviorSubject<boolean>(false);
  readonly isLoading$ = this.isLoading.asObservable();
  // simple store to hold data
  private store: BehaviorSubject<RouteStore> = new BehaviorSubject<RouteStore>({
    routeList: [],
    selectedRoute: null,
    availableRouteColors: [...ROUTE_COLORS],
    lastId: 0,
  });

  previousRouteState: RouteState[];
  stopTimeStamp: StopTimeStamp;
  isEuRegion: boolean;
  euProfileDefault: EUProfile;

  // shortcut to value
  private get storeValue(): RouteStore {
    return this.store.getValue();
  }
  readonly store$: Observable<RouteStore> = this.store.asObservable();

  // selectors into the store
  readonly routesState$: Observable<RouteState[]> = this.store$.pipe(
    map((s) => s.routeList),
    distinctUntilChanged()
  );

  readonly routeIds$: Observable<number[]> = this.routesState$.pipe(
    map((routes) => routes.map((route) => route.id)),
    distinctUntilChanged()
  );

  readonly routesCount$: Observable<number> = this.routesState$.pipe(
    map((routes) => routes?.length),
    distinctUntilChanged()
  );

  readonly selectedRoute$: Observable<RouteState> = this.store$.pipe(
    map((s) => s.selectedRoute),
    distinctUntilChanged()
  );

  readonly routes$: Observable<Route[]> = combineLatest([
    this.routesState$,
    this.selectedRoute$,
  ]).pipe(
    map(([routeList, sr]) =>
      routeList.map((rs) => this.transformToRoute(rs, sr))
    ),
    distinctUntilChanged()
  );

  readonly mapRoutes$: Observable<MapRoute[]> = combineLatest([
    this.routesState$,
    this.selectedRoute$,
    this.mapService.currentStyle$,
  ]).pipe(
    debounceTime(100),
    map(([routeList, sr, style]) =>
      routeList.map((rs) => this.transformToMapRoute(rs, sr, style))
    ),
    distinctUntilChanged((prev, curr) => isEqual(prev, curr))
  );

  // Add Import Stops
  private canAddImportStop: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  readonly canAddImportStop$: Observable<boolean> =
    this.canAddImportStop.asObservable();

  // non-state streams
  private deletedRouteId: Subject<number> = new Subject<number>();
  readonly deletedRouteId$: Observable<number> =
    this.deletedRouteId.asObservable();

  private deletedRoute: Subject<RouteState> = new Subject<RouteState>();
  readonly deletedRoute$: Observable<RouteState> =
    this.deletedRoute.asObservable();

  private frameRouteId: Subject<number> = new Subject<number>();
  readonly frameRouteId$: Observable<number> = this.frameRouteId.asObservable();

  private optimizeRoute: Subject<RouteOptimizationEvent> =
    new Subject<RouteOptimizationEvent>();
  readonly optimizeRoute$: Observable<RouteOptimizationEvent> =
    this.optimizeRoute.asObservable();

  subscriptionManager = new SubscriptionManager();
  dataVersions: NumericKeyValueState[] = [];
  dataVersionList$ = this.dataversionService.dataVersions;
  constructor(
    private mapService: MapService,
    private profileService: RoutingProfileService,
    private conversionService: ConversionService,
    private authenticationService: AuthenticationService,
    private http: HttpClient,
    private alertService: AlertService,
    private router: Router,
    private brazeService: BrazeService,
    private appSettingService: ApplicationSettingsService,
    private userService: UserService,
    private dataversionService: DataVersionService
  ) {
    this.subscriptionManager.manage =
      this.authenticationService.currentUser.subscribe((user) => {
        if (user) {
          this.isEuRegion = user?.region.toLowerCase() === Common.Region.EU;
          this.euProfileDefault = this.isEuRegion
            ? this.appSettingService.getDefaultAppSettingsData(this.isEuRegion)
                ?.euProfile
            : null;
        }
      });

    this.stopTimeStamp = null;
    const savedStore = this.getStorage();
    if (savedStore?.routeList) {
      savedStore.routeList.forEach((r) => {
        this.addRoute(r);
      });
    }
    this.dataVersionList$.subscribe((data) => {
      this.dataVersions = data;

      // Add a new route if the routeList is empty
      if (this.storeValue.routeList.length === 0) {
        this.newRouteFromDefaults();
      }

      // Select first route
      if (this.storeValue.routeList?.length > 0) {
        this.selectRoute(this.storeValue.routeList[0].id);
      }
    });

    // update storage when store changes
    this.subscriptionManager.manage = this.store$.subscribe(this.setStorage);
    this.subscriptionManager.manage = this.userService.afstore$.subscribe(
      (afsets) => {
        this.updateRouteForAFSet(afsets);
      }
    );
  }

  ngOnDestroy() {
    this.subscriptionManager.unsubscribe();
  }

  private setStore(newStore: RouteStore) {
    this.store.next(newStore);
  }

  private updateStore(newStore: Partial<RouteStore>) {
    this.previousRouteState = this.storeValue.routeList;
    this.store.next({
      ...this.storeValue,
      ...newStore,
    });
  }

  // selectors for route specific observables

  getRoute(routeId: number) {
    return this.routes$.pipe(
      mergeMap((routeList) =>
        routeList.filter((route) => route.id === routeId)
      ),
      distinctUntilChanged()
    );
  }

  getMapRoute(routeId: number) {
    return this.mapRoutes$.pipe(
      mergeMap((routeList) =>
        routeList.filter((route) => route.id === routeId)
      ),
      distinctUntilChanged((prev, curr) => isEqual(prev, curr))
    );
  }

  getStopList(routeId: number) {
    return this.getRoute(routeId).pipe(map((route) => route.stops));
  }

  // updaters

  // when adding a route, make it selcted, and open the route panel.
  addRoute(route: Partial<RouteState>) {
    const id = this.generateRouteId();
    const newRoute: RouteState = {
      ...route,
      id,
      name: route.name || `Route ${id}`,
      color: this.getRouteColor(route.color),
      euProfile: route?.euProfile ? route.euProfile : this.euProfileDefault,
      stopsState: route.stopsState || [],
      isOpen: true,
      profileId: route.profileId,
      savedId: route.savedId,
      profileDeleted: route.profileDeleted,
      editMode: RouteEditMode.AddStop, // always default new routes to AddStop mode
    };

    this.updateStore({
      routeList: [...this.storeValue.routeList, newRoute],
    });
    this.selectRoute(id);
  }

  duplicateRoute(routeId: number) {
    const route = this.storeValue.routeList.find((r) => r.id === routeId);
    const newRoute: RouteState = {
      ...route,
      name: `${route.name} - copy`,
      savedId: null,
      isSavedCheck: false,
      hasCountedForTrial: true,
      historicalDataVersion: route?.historicalDataVersion,
    };
    this.decrementRouteCount(newRoute);
    this.addRoute(newRoute);
  }

  deleteRoute(routeId: number) {
    const route = {
      ...this.storeValue.routeList.find((r) => r.id === routeId),
    };

    this.updateStore({
      routeList: [...this.storeValue.routeList.filter((r) => r.id !== routeId)],
    });

    this.returnRouteColor(route.color);

    if (route.id === this.storeValue.selectedRoute?.id) {
      this.clearSelectedRoute();
    }

    this.deletedRouteId.next(routeId);
    this.deletedRoute.next(route);
  }

  // deleteAllRoutes() {
  //   const allRoutes = this.storeValue.routeList;
  //   allRoutes.forEach((route) => {
  //     this.deleteRoute(route.id);
  //   });
  // }

  canImportStops(canImport: boolean) {
    this.canAddImportStop.next(canImport);
  }

  drawSavedRoute(trip: DaTrip.Trip, islegacy?: boolean) {
    let profileDeleted = false;
    if (trip.Profile?.routingProfileId !== null) {
      const profileName = trip.Profile.name;
      // const profileName = this.profileService.getProfileName(
      //   trip.Profile?.routingProfileId
      // );
      if (profileName === null) {
        profileDeleted = true;
      }
    }
    const availableProfileId = this.profileService.getProfileId(
      trip.Profile.name
    );
    //const pattern = /^(route [1-9][0-9]?[0-9]?[0-9]?)$/;

    const legacyRoutes = availableProfileId === -1;

    if (legacyRoutes) {
      const availableProfileByVehicleType =
        this.profileService.getAvailableProfileByVehicleType(
          trip.Profile.vehicleType
        );
      if (availableProfileByVehicleType) {
        trip.Profile.name = availableProfileByVehicleType.name;
      }
    }

    // getting the profileName from saved Trip and setting it to routeOptions and profileId based on profileName
    const routeOptionsFromTrip: RouteOptions = {
      profileName: trip.Profile.name,
      distanceUnits: trip.Profile.distanceUnits,
      tollDiscourage: trip.Profile.tollDiscouraged,
      dataVersion: this.authenticationService.currentUserValue?.dataset,
    };
    const profiles = JSON.parse(
      localStorage.getItem('routingProfileStore')
    ).profiles;
    const selectedProfile = profiles
      .filter((pf) => pf.name === trip.Profile?.name)
      .shift();
    const rs: Partial<RouteState> = {
      name: trip.Name,
      savedId: trip.Id,
      stopsState: this.conversionService.convertToStopLocations(trip.Stops),
      optionsState: this.conversionService.convertToRouteOptions(
        trip.Profile,
        selectedProfile?.routingProfileId
      ),
      isVisibleOnMap: true,
      profileId: this.profileService.getProfileId(trip.Profile.name), //trip.Profile?.routingProfileId,
      profileDeleted,
      hasCountedForTrial: true,
      isAcceptLegacy: false,
      isSavedCheck: islegacy ? false : true,
      isLegacy: false,
      historicalDataVersion: trip.Profile.dataVersion,
      tripProfileId: trip.Profile.routingProfileId,
    };
    if (this.isEuRegion) {
      rs.euProfile = trip?.Profile?.euProfile;
    }
    this.decrementRouteCount(rs);
    this.addRoute(rs);
  }

  newRouteFromDefaults() {
    // TODO get/generate default options
    if (this.isEuRegion || this.dataVersions?.length > 0) {
      const nonCurrentVersion = this.dataVersions?.find(function (item) {
        return item.value !== 'current';
      });

      this.addRoute({
        isVisibleOnMap: true,
        stopsState: [],
        optionsState: {},
        profileId: null,
        savedId: null,
        profileDeleted: false,
        hasCountedForTrial: false,
        isSavedCheck: false,
        isAcceptLegacy: false,
        isLegacy: false,
        historicalDataVersion:
          this.authenticationService.currentUserValue?.subscriptionLevel !==
          SubscriptionLevel.PREMIUM
            ? (nonCurrentVersion?.value as string)
            : this.authenticationService.currentUserValue?.dataset === 'PCM_NA'
            ? 'current'
            : this.authenticationService.currentUserValue?.dataset,
      });
    }
  }

  // TODO fix type
  updateRouteReports(
    routeId: number,
    {
      mileageReport,
      detailReport,
      drivingDirectionsReport,
      stateCountryReport,
      roadTypeReport,
      tollReport,
    }: any
  ) {
    this.updateStore({
      routeList: this.storeValue.routeList.map((r) => {
        return r.id === routeId
          ? {
              ...r,
              mileageReport,
              detailReport,
              drivingDirectionsReport,
              stateCountryReport,
              roadTypeReport,
              tollReport,
            }
          : r;
      }),
    });
  }

  selectRoute(routeId: number) {
    this.updateStore({
      selectedRoute: this.storeValue.routeList.find((r) => r.id === routeId),
    });
  }

  clearSelectedRoute() {
    const selectedRoute =
      this.storeValue.routeList?.length > 0
        ? this.storeValue.routeList[0]
        : null;
    const routeList =
      this.storeValue.routeList?.length > 0
        ? this.storeValue.routeList.map((r, i) => {
            return i === 0 ? { ...r, isOpen: true } : r;
          })
        : [];
    this.updateStore({
      routeList,
      selectedRoute,
    });
  }

  setRoutingProfile(routeId: number, profileId?: number) {
    const selectedRoute = this.storeValue.routeList
      .filter((rl) => rl.id === routeId)
      .shift();
    if (selectedRoute && selectedRoute.stopsState.length > 1) {
      this.setRouteLoadingState(true);
    }

    const selectedOptionState = {
      ...this.storeValue.selectedRoute.optionsState,
    };
    const profile = this.profileService.getProfiles().find((p) => {
      return p.routingProfileId === profileId;
    });
    const daVehicleType = this.conversionService.convertToProfileVehicleType(
      profile?.vehicleType
    );
    //profileName will handle
    selectedOptionState.vehicleType = this.getVehicleType(profile?.vehicleType);
    selectedOptionState.hazMatType = profile.vehicleOptions?.hazmatType
      ? profile.vehicleOptions?.hazmatType
      : 0;
    if (profile.routingOptions) {
      selectedOptionState.routeType = profile.routingOptions.routeType;
      selectedOptionState.tollRoads = profile.routingOptions.tollRoads;
      selectedOptionState.ferryDiscourage = profile.routingOptions.ferries
        ? true
        : false;
      selectedOptionState.bordersOpen = profile.routingOptions.useOpenBorders
        ? profile.routingOptions.useOpenBorders
        : false;
    }
    selectedOptionState.profileName = profile.name;
    selectedOptionState.profileId = profile.routingProfileId;
    selectedOptionState.dataVersion =
      this.authenticationService.currentUserValue?.dataset;

    // Truck Config
    const vehicleConfig = profile?.vehicleOptions?.vehicleConfig;
    if (vehicleConfig) {
      selectedOptionState.trkAxles = this.conversionService.getAxels(
        selectedOptionState?.vehicleType
      );
      selectedOptionState.trkUnits = profile?.units;
      selectedOptionState.trkHeight = vehicleConfig?.height;
      selectedOptionState.trkLength = vehicleConfig?.length;
      selectedOptionState.trkMaxWeightPerAxleGroup =
        vehicleConfig?.maxWeightPerAxleGroup;
      selectedOptionState.trkWeight = vehicleConfig?.weight;
      selectedOptionState.trkWidth = vehicleConfig?.width;
    }
    this.updateStore({
      routeList: this.storeValue.routeList.map((r) => {
        return r.id === routeId
          ? {
              ...r,
              profileId: profileId,
              optionsState: selectedOptionState,
              profileDeleted: false,
              isSavedCheck: false,
            }
          : r;
      }),
    });
  }

  getVehicleType(vehicleType: string) {
    switch (vehicleType) {
      case VehicleTypeEnum.Auto:
        return Common.VehicleType.AUTOMOBILE;
        break;
      case VehicleTypeEnum.LightDuty:
      case VehicleTypeEnum.MidsizeTruck:
      case VehicleTypeEnum.Bus:
        return Common.VehicleType.LIGHT_TRUCK;
        break;
      case VehicleTypeEnum.Truck:
        return Common.VehicleType.TRUCK;
        break;
      default:
        return Common.VehicleType.TRUCK;
        break;
    }
    // if (
    //   vehicleType === VehicleTypeEnum.Auto ||
    //   vehicleType === VehicleTypeEnum.Bus
    // ) {
    //   return Common.VehicleType.AUTOMOBILE;
    // }
    // if (
    //   vehicleType === VehicleTypeEnum.LightDuty ||
    //   vehicleType === VehicleTypeEnum.MidsizeTruck
    // ) {
    //   return Common.VehicleType.LIGHT_TRUCK;
    // }
    // if (vehicleType === VehicleTypeEnum.Truck) {
    //   return Common.VehicleType.TRUCK;
    // }
  }
  logMinDataVersion(version: string) {
    const userConfig = this.userService.getUserCfg();
    let minVersion = userConfig?.minDataVersionUsed
      ? userConfig?.minDataVersionUsed
      : 'current';
    if (this.isEuRegion) {
      minVersion = 'PCM_EU';
    } else {
      if (version) {
        const min = Number(minVersion?.substring(3));
        const dataVersion = Number(version?.substring(3));
        if (isNaN(min) || isNaN(dataVersion)) {
          minVersion =
            isNaN(min) && isNaN(dataVersion)
              ? 'current'
              : isNaN(min)
              ? version
              : minVersion;
        } else {
          minVersion = min < dataVersion ? `PCM${min}` : version;
        }
      }
    }
    const userCfg = { minDataVersionUsed: minVersion };
    this.userService.updateUserCfg(userCfg);
    this.brazeService.setAttr('Minimum Data Version Used', minVersion);
  }

  // to support historical data version
  setDataVersion(dataVersion, route: Route, routingProfile: RoutingProfile) {
    if (route.stops.length > 1) {
      this.setRouteLoadingState(true);
    }
    const currentRoute = this.storeValue.routeList
      .filter((rs) => rs.id === route.id)
      .shift();
    const profiles = JSON.parse(
      localStorage.getItem('routingProfileStore')
    ).profiles;
    const selectedProfile = profiles
      .filter((pf) => pf.routingProfileId === route?.profileId)
      .shift();

    //update  route options based on Application settings.|
    if (typeof currentRoute?.optionsState.distanceUnits === 'undefined') {
      currentRoute.optionsState.tollPlan = routingProfile?.tollDiscountPlans;
      currentRoute.optionsState.distanceUnits = routingProfile?.distanceUnits;
      currentRoute.optionsState.fuelUnits = routingProfile?.fuelUnits;
      currentRoute.optionsState.inclTollData = routingProfile?.includeTollData;
      currentRoute.optionsState.tollPlan = routingProfile?.tollDiscountPlans;
      currentRoute.optionsState.inclFerryDist =
        routingProfile?.includeFerryDistance;
      currentRoute.optionsState.fuelEconLoad = Number(
        routingProfile?.fuelEconomyLoad
      );
      currentRoute.optionsState.fuelEconEmpty = 0.0;
      currentRoute.optionsState.costPerFuelUnit = Number(
        routingProfile?.costPerFuelUnit
      );
      currentRoute.optionsState.costGHG = Number(routingProfile?.costGHG);
      currentRoute.optionsState.costMaintLoad = Number(
        routingProfile?.costMaintLoad
      );
      currentRoute.optionsState.costMaintEmpty = 0.0;
      currentRoute.optionsState.costTimeLoad = Number(
        routingProfile?.costTimeLoad
      );
      currentRoute.optionsState.costTimeEmpty = 0.0;
      currentRoute.optionsState.tollCurrency =
        routingProfile?.tollCurrency as (typeof Common.TollCurrency)[keyof typeof Common.TollCurrency];
      currentRoute.optionsState.exchangeRate = Number(
        routingProfile?.exchangeRate
      );
      currentRoute.optionsState.tollDiscourage =
        routingProfile?.tollDiscouraged;
      currentRoute.optionsState.useTraffic = routingProfile.useTraffic
        ? routingProfile.useTraffic
        : false;
      //for standard user highway only is true. so it is set here.
      currentRoute.optionsState.highwayOnly = routingProfile.highwayOnly;
    }
    //update routeoptions based on selected profile.\
    currentRoute.optionsState.vehicleType = this.getVehicleType(
      selectedProfile?.vehicleType
    );
    currentRoute.optionsState.governorSpeedLimit =
      selectedProfile?.vehicleOptions?.governorSpeedLimit;
    currentRoute.optionsState.hazMatType = selectedProfile.vehicleOptions
      ?.hazmatType
      ? selectedProfile.vehicleOptions?.hazmatType
      : 0;

    if (selectedProfile.routingOptions) {
      currentRoute.optionsState.routeType =
        selectedProfile.routingOptions.routeType;
      currentRoute.optionsState.tollRoads =
        selectedProfile.routingOptions.tollRoads;
      currentRoute.optionsState.ferryDiscourage = selectedProfile.routingOptions
        .ferries
        ? selectedProfile.routingOptions.ferries
        : false;
      currentRoute.optionsState.bordersOpen = selectedProfile.routingOptions
        .useOpenBorders
        ? selectedProfile.routingOptions.useOpenBorders
        : false;
      currentRoute.optionsState.elevLimit =
        selectedProfile?.routingOptions?.elevationLimit;
    }
    const daRouteOptions =
      this.conversionService.convertToDARouteOptionsFromRouteState(
        currentRoute.optionsState.profileName, // passing selected profileName,
        -1,
        currentRoute,
        selectedProfile
      );
    routingProfile = daRouteOptions;
    const historicalDataVersion = dataVersion;
    this.updateStore({
      routeList: this.storeValue.routeList.map((r) => {
        return r.id === route.id
          ? {
              ...r,
              historicalDataVersion,
              isSavedCheck: false,
            }
          : r;
      }),
    });

    routingProfile.dataVersion = dataVersion;
    const routeOption = this.conversionService.convertToRouteOptions(
      routingProfile,
      selectedProfile?.routingProfileId
    );
    routeOption.useAvoidsAndFavors = routeOption.afSetIDs.length > 0;
    this.isEuRegion =
      this.authenticationService.currentUserValue?.region.toLowerCase() ===
      Common.Region.EU;
    if (this.checkHistoricalDataVersion(dataVersion)) {
      this.generateHistoricalReport(routeOption, route.id, dataVersion);
    }
  }
  setRoutePanelExpandable(routeId: number) {
    const selectedRoute = this.storeValue.selectedRoute;
    const isOpen = routeId === selectedRoute.id ? !selectedRoute.isOpen : true;
    this.updateStore({
      routeList: this.storeValue.routeList.map((r) => {
        return r.id === routeId ? { ...r, isOpen } : r;
      }),
    });
    this.selectRoute(routeId);
  }

  // If a route is selected, when a route becomes visible, frame it.
  // If a route is not selected, when a route becomes visible, not frame it.
  setVisibilityOnMap(routeId: number, isVisible: boolean) {
    this.updateStore({
      routeList: this.storeValue.routeList.map((r) => {
        return r.id === routeId ? { ...r, isVisibleOnMap: isVisible } : r;
      }),
    });
  }

  frameRouteOnMap(routeId: number) {
    // ? should we check visibility here
    this.frameRouteId.next(routeId);
  }

  setRouteEditMode(routeId: number, mode: RouteEditMode) {
    this.updateStore({
      routeList: this.storeValue.routeList.map((r) => {
        return r.id === routeId ? { ...r, editMode: mode } : r;
      }),
    });
  }

  addStopToRoute(routeId: number, location: StopLocation) {
    const stop: StopState = {
      location,
      lngLat: location.coords,
    };
    this.updateStore({
      routeList: this.storeValue.routeList.map((r) => {
        return r.id === routeId
          ? { ...r, stopsState: [...r.stopsState, stop], isSavedCheck: false }
          : r;
      }),
    });
    const route = this.storeValue.routeList.find((r) => r.id === routeId);
    if (!route.hasCountedForTrial && route.stopsState.length === 2) {
      this.decrementRouteCount(route);
    }
    this.stopTimeStamp = { routeId, time: Date.now() };
  }

  deleteStopFromRoute(routeId: number, stopIndex: number) {
    this.updateStore({
      routeList: this.storeValue.routeList.map((r) => {
        if (r.stopsState.length <= 2) {
          this.setRouteLoadingState(false);
          r.hasCountedForTrial = r.stopsState.length < 2 ? false : true;
        }
        return r.id === routeId
          ? {
              ...r,
              stopsState: r.stopsState.filter((e, i) => i !== stopIndex),
              isSavedCheck: false,
            }
          : r;
      }),
    });
  }

  updateRouteAfterSave(
    routeId: number,
    savedId: number,
    name: string,
    tripProfileId: number
  ) {
    this.updateStore({
      routeList: this.storeValue.routeList.map((r) => {
        return r.id === routeId
          ? { ...r, savedId, name, tripProfileId, isSavedCheck: true }
          : r;
      }),
    });
  }
  updateRouteAfterUpdate(routeId: number) {
    this.updateStore({
      routeList: this.storeValue.routeList.map((r) => {
        return r.id === routeId ? { ...r, isSavedCheck: true } : r;
      }),
    });
  }
  appendRouteStopStates(routeId: number, locations: StopState[]) {
    this.updateStore({
      routeList: this.storeValue.routeList.map((r) => {
        const stopsState = [...r.stopsState, ...locations];
        return r.id === routeId ? { ...r, stopsState, isSavedCheck: false } : r;
      }),
    });
  }

  updateRouteStopStates(routeId: number, stopsState: StopState[]) {
    this.updateStore({
      routeList: this.storeValue.routeList.map((r) => {
        return r.id === routeId ? { ...r, stopsState, isSavedCheck: false } : r;
      }),
    });
  }

  updateRouteStops(routeId: number, locations: StopLocation[]) {
    const stopsState: StopState[] = locations.map((location) => {
      return {
        location,
        lngLat: location.coords,
      };
    });

    this.updateStore({
      routeList: this.storeValue.routeList.map((r) => {
        return r.id === routeId ? { ...r, stopsState, isSavedCheck: false } : r;
      }),
    });
  }

  updateRouteStopTime(routeId: number, stopTimes: string[]) {
    this.updateStore({
      routeList: this.storeValue.routeList.map((r) => {
        const stopsState = r.stopsState.map((stopState, idx) => {
          const hasZero = stopTimes[idx]?.includes(':')
            ? Number(stopTimes[idx].replace(':', '')) === 0
            : Number(stopTimes[idx]) === 0;
          return {
            ...stopState,
            stopTime: hasZero ? '' : stopTimes[idx],
          };
        });
        return r.id === routeId ? { ...r, stopsState } : r;
      }),
    });
  }

  updateAfterDeleteSavedRoute(routeId: number) {
    const savedId = null;
    this.updateStore({
      routeList: this.storeValue.routeList.map((r) => {
        return r.savedId === routeId
          ? { ...r, savedId, isSavedCheck: false }
          : r;
      }),
    });
  }

  updateLegacyProfileShowInfo(routeId: number) {
    this.updateStore({
      routeList: this.storeValue.routeList.map((r) => {
        return r.id === routeId
          ? { ...r, isAcceptLegacy: true, isSavedCheck: true }
          : r;
      }),
    });
  }

  //update store based on application settings
  updateStoreFromApplicationSettings(data) {
    this.updateStore({
      routeList: this.storeValue.routeList.map((r) => {
        r.optionsState.costPerFuelUnit = Number(data?.costPerFuelUnit);
        r.optionsState.fuelEconLoad = Number(data?.fuelEconomyLoad);
        r.optionsState.fuelEconEmpty = 0.0;
        r.optionsState.costMaintLoad = Number(data?.costMaintLoad);
        r.optionsState.costMaintEmpty = 0.0;
        r.optionsState.costTimeLoad = Number(data?.costTimeLoad);
        r.optionsState.costTimeEmpty = 0.0;
        r.optionsState.costGHG = Number(data?.costGHG);
        //setting toll cost values
        r.optionsState.inclTollData = data?.includeTollData;
        r.optionsState.tollPlan = data?.tollDiscountPlans;

        //setting currency & unit values
        r.optionsState.tollCurrency =
          data.tollCurrency as (typeof Common.TollCurrency)[keyof typeof Common.TollCurrency];

        r.optionsState.exchangeRate = Number(data?.exchangeRate);

        r.optionsState.distanceUnits = data?.distanceUnits;

        r.optionsState.fuelUnits = data?.fuelUnits;

        // other options
        r.optionsState.tollDiscourage =
          typeof r?.optionsState?.tollDiscourage === 'undefined'
            ? data?.tollDiscouraged
            : r?.optionsState?.tollDiscourage;

        r.optionsState.useTraffic =
          typeof r?.optionsState?.useTraffic === 'undefined'
            ? data?.useTraffic
              ? data?.useTraffic
              : false
            : r?.optionsState?.useTraffic;

        //for standard user highway only is true. so it is set here.
        r.optionsState.highwayOnly =
          typeof r?.optionsState?.highwayOnly === 'undefined'
            ? data?.highwayOnly
            : r?.optionsState?.highwayOnly;

        return { ...r, isSavedCheck: false };
      }),
    });
  }

  rearrangeRouteStops(routeId: number, fromIndex: number, toIndex: number) {
    const rt = cloneDeep(
      this.storeValue.routeList.find((route) => route.id === routeId)
    );
    moveItemInArray(rt.stopsState, fromIndex, toIndex);
    this.updateStore({
      routeList: this.storeValue.routeList.map((r) => {
        return r.id === routeId
          ? {
              ...r,
              stopsState: rt.stopsState,
              isSavedCheck: false,
            }
          : r;
      }),
    });
  }

  addStopToSelectedRoute(location: StopLocation) {
    if (location.address) {
      location.address.streetAddress =
        this.authenticationService.currentUserValue.subscriptionLevel ===
        SubscriptionLevel.PREMIUM
          ? location.address?.streetAddress
          : '';
    }
    const id = this.storeValue.selectedRoute?.id;
    if (id) {
      this.addStopToRoute(id, location);
    }
  }

  setRouteOptimization(routeId: number, routeOptimization: RouteOptimization) {
    this.setRouteLoadingState(true);
    this.optimizeRoute.next({ routeId, optimization: routeOptimization });
    this.updateStore({
      routeList: this.storeValue.routeList.map((r) => {
        return r.id === routeId
          ? {
              ...r,
              isSavedCheck: false,
            }
          : r;
      }),
    });
  }
  updateRouteForAFSet(afset: []) {
    if (afset) {
      const afsetIds = afset?.map((af) => {
        return af;
      });
      // const afsetNames = afset.map((af) => {
      //   return af.name;
      // });
      this.updateStore({
        routeList: this.storeValue.routeList.map((r) => {
          const useTraffic = afset;
          const optionsState = {
            ...r.optionsState,
            afSetIDs: afsetIds,
            afSetNames: [],
          };
          return { ...r, optionsState };
        }),
      });
    }
  }
  // Helpers for work inside the service

  getRouteColor(color?: string) {
    // support getting specific colors for rehydrating persisted route
    // otherwise get the next available

    let newColor = '';
    const availableRouteColors = [...this.storeValue.availableRouteColors];

    if (color) {
      // check availability
      if (availableRouteColors.includes(color)) {
        availableRouteColors.splice(availableRouteColors.indexOf(color), 1);
        newColor = color;
      } else if (ROUTE_COLORS.includes(color)) {
        // color is valid, but not available
        newColor = availableRouteColors.shift();
      } else {
        // custom color
        newColor = color;
      }
    } else {
      // grab the next color
      newColor = availableRouteColors.shift();
    }

    // update store
    this.updateStore({
      availableRouteColors,
    });

    return newColor;
  }

  returnRouteColor(color: string) {
    const availableRouteColors = [...this.storeValue.availableRouteColors];

    if (ROUTE_COLORS.includes(color) && !availableRouteColors.includes(color)) {
      availableRouteColors.push(color);

      this.updateStore({
        availableRouteColors,
      });
    }
  }

  generateRouteId() {
    const lastRouteId = this.storeValue.lastId + 1;

    this.updateStore({
      lastId: lastRouteId,
    });

    return lastRouteId;
  }

  setStorage(store: RouteStore) {
    if (sessionStorage) {
      // filter down what we want to persist

      const routeList = store.routeList.map((r) => {
        // clear properties that don't need to be persisted
        const { id, mileageReport, ...storageRoute } = r;

        return storageRoute;
      });

      sessionStorage.setItem('routeStore', JSON.stringify({ routeList }));
    }
  }

  getStorage(): RouteStore {
    return sessionStorage
      ? JSON.parse(sessionStorage.getItem('routeStore'))
      : {};
  }

  // TODO clear storage on logout
  clearStorage() {
    if (sessionStorage) {
      sessionStorage.removeItem('routeStore');
    }
  }

  // Internal methods to convert state objects to view models
  // These should be private and stateless. They could live somewhere else

  private transformToRoute(routeState: RouteState, selectedRoute: RouteState) {
    const isSelected = routeState.id === selectedRoute?.id;
    const lastLine =
      routeState.mileageReport?.ReportLines[
        routeState.mileageReport.ReportLines.length - 1
      ];
    let profileName: string = null;
    let profile: RoutingProfileModel;
    if (!routeState.profileDeleted) {
      profile = this.profileService.getProfile(routeState.profileId);
      profileName = profile?.name;
    }
    const afsetIds = this.userService.getSelectedAFSets()?.map((af) => {
      return af;
    });
    // const afsetNames = this.userService.getSelectedAFSets()?.map((af) => {
    //   return af.name;
    // });
    routeState.optionsState.afSetIDs = afsetIds;
    // routeState.optionsState.afSetNames = afsetNames;

    let optionSummary = profileName; // and link to information about the profile. // Todo-yz
    if (profileName === null) {
      optionSummary = this.conversionService.getRouteOptionSummary(routeState);
    }
    const historicalDataVersion = routeState?.historicalDataVersion;
    let arrDepSelection: ArrDepSelection;
    if (routeState.optionsState.estimatedTimeOpts) {
      arrDepSelection = deserializeArrDepSelection(
        routeState.optionsState.estimatedTimeOpts
      );
    }
    if (typeof arrDepSelection !== 'undefined' && arrDepSelection !== null) {
      arrDepSelection.useTraffic = routeState.optionsState.useTraffic;
    }

    return {
      id: routeState.id,
      name: routeState.name,
      color: routeState.color,
      isSelected,
      isOpen: routeState.isOpen,
      isLegacy: routeState.isLegacy,
      isAcceptLegacy: routeState.isAcceptLegacy,
      isSavedCheck: routeState.isSavedCheck,
      isVisibleOnMap: routeState.isVisibleOnMap,
      editMode: routeState.editMode,
      stops: routeState.stopsState.map((s, i) =>
        this.transformToRouteStop(s, i, routeState)
      ),
      optionSummary,
      totals: lastLine
        ? {
            cost: lastLine?.TCostMile,
            distance: lastLine?.TMiles,
            time: lastLine?.THours,
            tolls: lastLine?.TTolls,
          }
        : null,
      profileId: routeState.profileDeleted ? -1 : routeState.profileId,
      savedId: routeState.savedId,
      profileDeleted: routeState.profileDeleted,
      arrDepSelection,
      historicalDataVersion,
    } as Route;
  }

  private transformToMapRoute(
    routeState: RouteState,
    selectedRoute: RouteState,
    style: string
  ) {
    const isSelected = routeState?.id === selectedRoute?.id;
    this.subscriptionManager.manage = this.appSettingService.store$.subscribe(
      (data) => {
        if (
          typeof routeState.optionsState.distanceUnits === 'undefined' &&
          data
        ) {
          routeState.optionsState.costPerFuelUnit = Number(
            data?.costPerFuelUnit
          );
          routeState.optionsState.fuelEconLoad = Number(data?.fuelEconomyLoad);
          routeState.optionsState.fuelEconEmpty = 0.0;
          routeState.optionsState.costMaintLoad = Number(data?.costMaintLoad);
          routeState.optionsState.costMaintEmpty = 0.0;
          routeState.optionsState.costTimeLoad = Number(data?.costTimeLoad);
          routeState.optionsState.costTimeEmpty = 0.0;
          routeState.optionsState.costGHG = Number(data?.costGHG);
          //setting toll cost values
          routeState.optionsState.inclTollData = data?.includeTollData;
          routeState.optionsState.tollPlan = data?.tollDiscountPlans;

          //setting currency & unit values
          routeState.optionsState.tollCurrency =
            data?.tollCurrency as (typeof Common.TollCurrency)[keyof typeof Common.TollCurrency];

          routeState.optionsState.exchangeRate = Number(data?.exchangeRate);

          routeState.optionsState.distanceUnits = data?.distanceUnits;

          routeState.optionsState.fuelUnits = data?.fuelUnits;

          // other options
          routeState.optionsState.tollDiscourage = data?.tollDiscouraged;
          routeState.optionsState.useTraffic = data?.useTraffic
            ? data.useTraffic
            : false;

          //for standard user highway only is true. so it is set here.
          routeState.optionsState.highwayOnly = data?.highwayOnly;
        }
      }
    );
    const opacity = routePathOpacity(isSelected, style);
    let reportType = [];
    reportType = [
      TrimbleMaps.Common.ReportType.MILEAGE,
      TrimbleMaps.Common.ReportType.DETAIL,
      TrimbleMaps.Common.ReportType.DIRECTIONS,
      TrimbleMaps.Common.ReportType.STATE,
      TrimbleMaps.Common.ReportType.ROAD,
    ];
    this.isEuRegion =
      this.authenticationService.currentUserValue?.region.toLowerCase() ===
      Common.Region.EU;
    if (this.isEuRegion) {
      reportType.push(TrimbleMaps.Common.ReportType.TOLL);
    }

    // frame route only when a route is selected. When a route becomes inactive and the other becomes active, we don't want to frame the inactive route.
    routeState.optionsState.frameRoute = isSelected;
    let profileName: string = null;
    let profileId: number;
    if (!routeState.profileDeleted) {
      const profile = this.profileService.getProfile(routeState.profileId);
      profileName = profile?.name;
      profileId = profile?.routingProfileId;
    }
    // In the SDK, PCM WS doesn't allow null profile name.
    delete routeState.optionsState.profileName;
    if (profileName !== null) {
      routeState.optionsState.profileName = profileName;
      routeState.optionsState.profileId = profileId;
    }
    return {
      id: routeState.id,
      isVisibleOnMap: routeState.isVisibleOnMap,
      historicalDataVersion: routeState.historicalDataVersion,
      options: {
        ...routeState.optionsState,
        routeId: routeState.id.toString(),
        routeColor: routeState.color,
        routePathOpacity: opacity,
        stops:
          routeState.stopsState.length === 0
            ? null
            : routeState.stopsState.map((s) => {
                return {
                  lngLat: TrimbleMaps.LngLat.convert(s.lngLat),
                  address: {
                    streetAddress:
                      this.authenticationService.currentUserValue
                        .subscriptionLevel === SubscriptionLevel.PREMIUM
                        ? s.location.address?.streetAddress
                        : '',
                    city: s.location.address?.city,
                    state: s.location.address?.stateAbbreviation,
                    zip: s.location.address?.zip,
                    county: s.location.address?.county,
                    country: s.location.address?.countryAbbreviation,
                  },
                  costs: {
                    costOfStop: 0.0,
                    hoursPerStop: getHoursFromString(s.stopTime),
                    loaded: true,
                    onDuty: true,
                    useOrigin: false,
                  },
                };
              }),
        reportType: reportType,
        requestMethod: TrimbleMaps.Common.RequestMethod.POST,
        dataVersion:
          Common.DataVersion[
            this.authenticationService.currentUserValue.region
          ],

        routeOptimization: TrimbleMaps.Common.RouteOptimization.NONE, // always NONE. overridden by action when needed.
      },
    } as MapRoute;
  }

  private transformToRouteStop(
    stopState: StopState,
    index: number,
    routeState: RouteState
  ) {
    const loc: StopLocation = stopState.location || {};
    let stopType = 'S';
    if (index === 0) {
      stopType = 'O';
    } else if (index === routeState.stopsState.length - 1) {
      stopType = 'D';
    }

    // get report data if available
    const reportLine = routeState.mileageReport?.ReportLines[index];
    return {
      lngLat: stopState.lngLat,
      name: loc?.name,
      address: {
        streetAddress: loc.address?.streetAddress,
        city: loc.address?.city,
        state: loc.address?.stateAbbreviation,
        zip: loc.address?.zip,
        county: loc.address?.county,
        country: loc.address?.countryAbbreviation,
        SPLC: null,
      },
      stopType,
      tolls: reportLine?.LTolls,
      etaEtd: getEtaEtdDateTime(reportLine?.EtaEtd), // null or object
      miles: reportLine?.LMiles,
      hours: reportLine?.LHours,
      stopTime: stopState.stopTime,
    } as RouteStop;
  }

  setArrivalDepartureRouteOptions(routeId: number, data?: ArrDepSelection) {
    const estimatedTimeOpts = serializeArrDepSelection(data);
    // const dd = deserializeArrDepSelection(estimatedTimeOpts);

    this.updateStore({
      routeList: this.storeValue.routeList.map((r) => {
        const useTraffic = data.useTraffic;
        const optionsState = {
          ...r.optionsState,
          estimatedTimeOpts,
          useTraffic,
        };
        return r.id === routeId ? { ...r, optionsState } : r;
      }),
    });
  }
  decrementRouteCount(selectedRoute?: Partial<RouteState>): void {
    const user = this.authenticationService.currentUserValue;
    if (
      this.authenticationService.isTrialUser(user) &&
      user.remainingRoutes > 0
    ) {
      const v1baseUrl = environment.pcmilerAppUrl.replace('/v2', '/v1');
      this.subscriptionManager.manage = this.http
        .post<{ RemainingRoutes: number }>(
          `${v1baseUrl}api/UserInfo/IncrementAPIRouteCount`,
          null
        )
        .subscribe((val: { RemainingRoutes: number }) => {
          user.remainingRoutes = val?.RemainingRoutes;
          this.authenticationService.setCurrentUser(user);
          this.brazeService.trackRemainingRoutes(val?.RemainingRoutes);
          if (selectedRoute) {
            selectedRoute.hasCountedForTrial = true;
            this.updateStore({
              routeList: this.storeValue.routeList.map((r) => {
                return r.id === selectedRoute.id
                  ? { ...r, hasCountedForTrial: true }
                  : r;
              }),
            });
          }
        });
    } else if (
      this.authenticationService.isTrialUser(user) &&
      user.remainingRoutes <= 0
    ) {
      this.router.navigate(['/accountSettings/myAccount']);
    }
  }

  setRouteLoadingState(isRouteLoading: boolean) {
    this.isLoading.next(isRouteLoading);
  }
  showOpenRoutePanel() {
    return !this.authenticationService.isPromoUser();
  }
  generateHistoricalReport(
    routeOption: Omit<TrimbleMaps.RouteOptions, 'dataVersion'> & {
      dataVersion?: string;
    },
    routeId: number,
    dataVersion: string
  ) {
    const selectedRouteState = this.storeValue?.routeList?.filter(
      (pf) => pf.id === routeId
    );

    const estimatedTimeOptions = this.estimatedTimeOpts(
      selectedRouteState[0]?.optionsState?.estimatedTimeOpts
    );
    const routeOptionForReport = { ...routeOption };
    if (
      routeOptionForReport.vehicleType ===
      TrimbleMaps.Common.VehicleType.AUTOMOBILE
    ) {
      routeOptionForReport.trkLCV = undefined;
      routeOptionForReport.trkUnits = undefined;
      routeOptionForReport.trkHeight = undefined;
      routeOptionForReport.trkLength = undefined;
      routeOptionForReport.trkWidth = undefined;
      routeOptionForReport.trkMaxWeightPerAxleGroup = undefined;
      routeOptionForReport.trkTireCount = undefined;
      routeOptionForReport.trkWeight = undefined;
      routeOptionForReport.trkAxles = undefined;
    }
    const routeReportOptions =
      this.conversionService.createRoutingReportOptions(routeOptionForReport);
    const reportOption = {
      ReportRoutes: [
        {
          ReportingOptions: {
            EstimatedTimeOptions: estimatedTimeOptions,
            // RouteCosts: {
            //   FuelEconomyLoaded: routeOption?.fuelEconLoad,
            //   FuelEconomyEmpty: routeOption?.fuelEconEmpty,
            //   PricePerFuelUnit: routeOption?.costPerFuelUnit,
            //   GreenHouseGas: routeOption?.costGHG,
            //   OtherCostPerDistUnitLoaded: routeOption?.costMaintLoad,
            //   OtherCostPerDistanceUnitEmpty: routeOption?.costMaintEmpty,
            //   CostTimeLoaded: routeOption?.costTimeLoad,
            //   CostTimeEmpty: routeOption?.costTimeEmpty,
            // },
            IncludeFerryDistance: routeOption?.inclFerryDist,
            UseTraffic: selectedRouteState[0]?.optionsState?.useTraffic,
            ExchangeRate: routeOption?.exchangeRate,
            FuelUnits: routeOption?.fuelUnits,
            TollCurrency: routeOption?.tollCurrency,
            TollDiscount: routeOption?.tollPlan,
            UseTollData: routeOption?.inclTollData,
            UseCustomRoadSpeeds: routeOption?.custRdSpeeds,
            TruckStyle: routeOption.truckConfig,
          },
          ReportTypes: [
            {
              __type: 'MileageReportType:http://pcmiler.alk.com/APIs/v1.0',
            },
            {
              __type: 'DetailReportType:http://pcmiler.alk.com/APIs/v1.0',
            },
            {
              __type: 'DirectionsReportType:http://pcmiler.alk.com/APIs/v1.0',
            },
            {
              __type: 'StateReportType:http://pcmiler.alk.com/APIs/v1.0',
            },
            {
              __type: 'RoadReportType:http://pcmiler.alk.com/APIs/v1.0',
            },
          ],
          Stops:
            selectedRouteState[0]?.stopsState?.length === 0
              ? null
              : selectedRouteState[0]?.stopsState?.map((s) => {
                  return {
                    Address: {
                      AbbreviationFormat: 0,
                      StreetAddress:
                        this.authenticationService.currentUserValue
                          .subscriptionLevel === SubscriptionLevel.PREMIUM
                          ? s.location.address?.streetAddress
                          : '',
                      City: s.location.address?.city,
                      CountryAbbreviation: null,
                      CountryPostalFilter:
                        this.authenticationService.currentUserValue?.dataset !==
                        'PCM_EU'
                          ? 2
                          : null, // To handle both US and Mexico zipcode.
                      SPLC: null,
                      StateAbbreviation: null,
                      StateName: null,
                      State: s.location.address?.stateAbbreviation,
                      Zip: s.location.address?.zip,
                      County: s.location.address?.county,
                      Country: s.location.address?.countryAbbreviation,
                    },
                    Coords: { Lat: s?.lngLat[1], Lon: s?.lngLat[0] },
                    Costs: {
                      CostOfStop: 0.0,
                      HoursPerStop: getHoursFromString(s.stopTime),
                      Loaded: true,
                      OnDuty: true,
                      UseOrigin: false,
                    },
                    PlaceName: null,
                  };
                }),

          Options: routeReportOptions,
          RouteId: routeId.toString(),
        },
      ],
    };
    if (reportOption.ReportRoutes[0].Stops) {
      //check dataversion is 18/ 19 and remove specific report types
      if (dataVersion === 'PCM18' || dataVersion === 'PCM19') {
        reportOption.ReportRoutes[0].ReportTypes =
          reportOption.ReportRoutes[0].ReportTypes.filter((rt) => {
            return (
              rt.__type.includes('MileageReportType') ||
              rt.__type.includes('DirectionsReportType')
            );
          });
      }
      this.http
        .post(
          `${environment.pcmwsUrl}/route/routeReports?dataVersion=${dataVersion}`,
          reportOption
        )
        .subscribe(
          (response) => {
            const report = {
              mileageReport: response[0],
              detailReport: response[1],
              drivingDirectionsReport: response[2],
              stateCountryReport: response[3],
              roadTypeReport: response[4],
              tollReport: response[5],
            };
            const selectedOptionState =
              this.storeValue.selectedRoute.optionsState;
            selectedOptionState.dataVersion = dataVersion;
            this.updateStore({
              routeList: this.storeValue.routeList.map((r) => {
                return r.id === routeId
                  ? {
                      ...r,
                      historicalDataVersion: dataVersion,
                    }
                  : r;
              }),
            });
            this.updateRouteReports(routeId, report);
            this.setRouteLoadingState(false);
            this.alertService.clear();
          },
          (err) => {
            if (routeId === this.storeValue.selectedRoute.id) {
              const errorMessage =
                'Due to a technical issue, services are temporarily unavailable.  Please check back again for an updated status.';
              this.alertService.warning(
                `${errorMessage}`,
                true,
                'app-modal-alert',
                true
              );
            }
            this.setRouteLoadingState(false);
          }
        );
    }
  }
  checkHistoricalDataVersion(dataVersion: string): boolean {
    if (dataVersion) {
      const isHistoricalDV = !isNaN(Number(dataVersion.substring(3, 5)))
        ? true
        : false;
      return isHistoricalDV;
    }
  }

  estimatedTimeOpts(estimatedTimeOpts: string): EstimatedTimeOptionsStruct {
    if (estimatedTimeOpts) {
      const etParts = estimatedTimeOpts?.split('|');
      const etOpts: EstimatedTimeOptionsStruct = {};
      etOpts.DateOption = Common.DateOption.CURRENT;
      let keys;
      if (etParts[0] === 'Arrive' || etParts[0] === 'Depart') {
        etOpts.ETAETD =
          etParts[0] === 'Arrive'
            ? Common.ETAETDType.ARRIVAL
            : Common.ETAETDType.DEPART;
        if (etParts.length > 1) {
          keys = Object.keys(Common.DayOfWeek).map((k) => k.toLowerCase()); // [mondy, tusday, ...]
          let day = etParts[1] || ''; // 'monday'
          day = day.toLowerCase();
          if (keys.includes(day)) {
            if (etParts.length > 2) {
              etOpts.DateOption = Common.DateOption.DAY_OF_WEEK;
              const dtType: DateTimeType = {};
              dtType.dayOfWeek = getDayOfWeek(day);
              etOpts.DateAndTime = dtType;
              // assume the 3rd part is time of day in (HH:mm) 24 hour format
              etOpts.DateAndTime.timeOfDay = etParts[2];
            } else {
              console.warn(
                'Day of week was specified, but time was not, for ex: Arrive|Monday|18:30'
              );
            }
          } else {
            let timeZone = Common.TimeZone.LOCAL;
            if (etParts[1]) {
              keys = Object.keys(Common.TimeZone).map((k) => k.toLowerCase()); // [local, system, ...]
              let tz = etParts[2] || ''; // local
              tz = tz.toLowerCase();
              if (etParts.length === 3 && !keys.includes(tz)) {
                console.warn('Invalid TimeZone specified.');
              }
              timeZone = getTimeZone(tz);
              const specificTime = new Date(etParts[1]); // new Date("2015-03-25T12:00:00-06:00");
              etOpts.DateOption = Common.DateOption.SPECIFIC;

              const st = specificTime.toLocaleTimeString('en-US'); // ??
              const stItems = st.split(' ');
              const timeItems = stItems[0].split(':');
              const timeOfDay = `${timeItems[0]}:${timeItems[1]} ${stItems[1]}`;

              const dtType = {
                CalendarDate: specificTime.toLocaleDateString(), // 6/1/2009
                DayOfWeek: specificTime.getDay(), // 0 - 7, sunsay
                TimeOfDay: timeOfDay, // 8:20 AM
                TimeZone: timeZone,
              };
              etOpts.DateAndTime = dtType;
            } else {
              console.warn(
                'Specific time must be in the ISO8601 format, ex: 2015-03-25T12:00:00-06:00'
              );
            }
          }
        }
      } else {
        console.warn(
          'Estimated time options must begin with Arrive or Depart.'
        );
      }
      return etOpts;
    }

    return null;
  }
}
